import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Receipt, MessageCircle, Mail, Landmark, AlertOctagon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { AlertBanner } from '@/components/ui/alert-banner';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { cn, formatCurrency, formatDateOnly } from '@/lib/utils';
import { parseLineItems, hasPayChannel, type SelfBill, type SelfBillsResponse } from '@/lib/billing';
import api from '@/lib/api';
import { billLineLabel } from '@/lib/catalog-labels';

/** Tighter cell padding in the tablet band so the 4 remaining columns fit at 768px (incl. RTL). */
const TIGHT = 'md:max-lg:px-2.5';
/** Period + Items columns: hidden between 768-1023px (Period folds under the bill number); mobile cards keep them. */
const WIDE_ONLY = 'md:max-lg:hidden';

/**
 * T-447 — tenant "My Bills": read-only list of the tenant's own subscription
 * bills plus the WhatsApp / bank-transfer / email ways to settle them. All
 * payment links come pre-built from the server (`paymentContact`); when the
 * founder has not configured a number or bank, those blocks are hidden and
 * only the mailto fallback remains.
 */
export function MyBillsPage() {
  const { t } = useTranslation('settings');

  const { data, isLoading, isError } = useQuery<SelfBillsResponse>({
    queryKey: ['my-subscription-bills'],
    queryFn: () => api.get('/subscription/bills').then((r) => r.data),
    staleTime: 30_000,
  });

  const bills: SelfBill[] = data?.bills ?? [];
  const contact = data?.paymentContact;
  const overdueCount = bills.filter((b) => b.status === 'OVERDUE').length;
  const statusLabel = (status: string): string => t(`billStatus_${status}`, { defaultValue: status });

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title={t('myBillsTitle')} subtitle={t('myBillsSubtitle')} />

      {overdueCount > 0 && (
        <AlertBanner tone="danger" icon={AlertOctagon}>
          <strong>{t('myBillsOverdueTitle')}</strong>
          {' — '}
          {t('myBillsOverdueBody', { count: overdueCount })}
        </AlertBanner>
      )}

      {isError && <AlertBanner tone="danger">{t('myBillsLoadError')}</AlertBanner>}

      {contact && (
        <Card className="animate-fade-in-up">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('billPayTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {hasPayChannel(contact) ? t('billPayHint') : t('billPayHintEmailOnly')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {contact.whatsappUrl && (
                <Button asChild>
                  <a href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4" />
                    {t('billPayWhatsApp')}
                  </a>
                </Button>
              )}
              <Button asChild variant="outline">
                <a href={`mailto:${contact.supportEmail}`}>
                  <Mail className="h-4 w-4" />
                  {t('billEmailSupport')}
                </a>
              </Button>
            </div>
            {contact.bank && (
              <div className="rounded-xl border p-4">
                <p className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <Landmark className="h-4 w-4 text-primary-text" />
                  {t('billBankTitle')}
                </p>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
                  {contact.bank.title && (
                    <>
                      <dt className="text-muted-foreground">{t('billBankAccountTitle')}</dt>
                      <dd className="font-medium">{contact.bank.title}</dd>
                    </>
                  )}
                  {contact.bank.iban && (
                    <>
                      <dt className="text-muted-foreground">{t('billBankIban')}</dt>
                      <dd className="font-mono font-medium break-all" dir="ltr">{contact.bank.iban}</dd>
                    </>
                  )}
                  {contact.bank.name && (
                    <>
                      <dt className="text-muted-foreground">{t('billBankName')}</dt>
                      <dd className="font-medium">{contact.bank.name}</dd>
                    </>
                  )}
                </dl>
                <p className="mt-3 text-xs text-muted-foreground">{t('billBankReferenceNote')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <SkeletonTable rows={4} cols={6} />
            </div>
          ) : bills.length === 0 ? (
            <EmptyState icon={Receipt} title={t('myBillsEmpty')} subtitle={t('myBillsEmptyHint')} />
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th className={TIGHT}>{t('billColNumber')}</Th>
                  <Th className={cn(TIGHT, WIDE_ONLY)}>{t('billColPeriod')}</Th>
                  <Th className={cn(TIGHT, WIDE_ONLY)}>{t('billColItems')}</Th>
                  <Th className={cn(TIGHT, 'whitespace-nowrap')}>{t('billColAmount')}</Th>
                  <Th className={cn(TIGHT, 'whitespace-nowrap')}>{t('billColDue')}</Th>
                  <Th className={cn(TIGHT, 'whitespace-nowrap')}>{t('billColStatus')}</Th>
                </Tr>
              </THead>
              <TBody>
                {bills.map((bill) => {
                  const isOverdue = bill.status === 'OVERDUE';
                  const items = parseLineItems(bill.lineItems);
                  return (
                    <Tr key={bill.id} className={cn(isOverdue && '!bg-destructive/5 max-md:!border-destructive/40')}>
                      <Td className={cn(TIGHT, 'td-name')}>
                        <span className="block font-mono text-xs font-semibold whitespace-nowrap" dir="ltr">{bill.billNumber}</span>
                        {/* Tablet band (768-1023px): Period + Items columns collapse under the number so the table fits. */}
                        <span className="hidden md:block lg:hidden mt-0.5 text-xs font-normal text-muted-foreground">
                          <bdi dir="ltr">{formatDateOnly(bill.billingPeriodStart)}</bdi> – <bdi dir="ltr">{formatDateOnly(bill.billingPeriodEnd)}</bdi>
                        </span>
                      </Td>
                      <Td label={t('billColPeriod')} className={cn(TIGHT, WIDE_ONLY, 'whitespace-nowrap')}>
                        <bdi dir="ltr">{formatDateOnly(bill.billingPeriodStart)}</bdi> – <bdi dir="ltr">{formatDateOnly(bill.billingPeriodEnd)}</bdi>
                      </Td>
                      <Td label={t('billColItems')} className={cn(TIGHT, WIDE_ONLY)}>
                        {items.length === 0 ? (
                          '—'
                        ) : (
                          <ul className="w-full space-y-0.5 text-xs">
                            {items.map((li, i) => (
                              <li key={`${li.description}-${i}`} className="flex justify-between gap-4">
                                <span>{billLineLabel(li.description)}</span>
                                <span className="text-muted-foreground">{formatCurrency(li.amount, bill.currency)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </Td>
                      <Td label={t('billColAmount')} className={cn(TIGHT, 'font-semibold whitespace-nowrap')}>
                        {formatCurrency(bill.totalAmount, bill.currency)}
                      </Td>
                      <Td
                        label={t('billColDue')}
                        className={cn(TIGHT, 'whitespace-nowrap', isOverdue && 'font-medium text-destructive-text')}
                      >
                        {/* One wrapper so the mobile card's flex row has a single value child, end-aligned like the other rows. */}
                        <div className="max-md:text-end">
                          <bdi dir="ltr">{formatDateOnly(bill.dueDate)}</bdi>
                          {bill.status === 'PAID' && bill.paidAt && (
                            <span className="block text-xs text-muted-foreground">
                              {t('billPaidOn', { date: `⁦${formatDateOnly(bill.paidAt)}⁩` })}
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td label={t('billColStatus')} className={TIGHT}>
                        <StatusBadge status={bill.status} label={statusLabel(bill.status)} className="whitespace-nowrap" />
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
